import { List } from "immutable"
import { EventDetails, EventDetailsFrontend, Image, Location, Ticket } from "../../../backendApi/generated"
import { Text } from "../../../components/Text"
import { features } from "../../../features"
import { defaultCalendarSpec, endTimeCalendarSpec, moment } from "../../../utils/moment"
import { capitalize, slugify } from "../../../utils/string"
import { Container } from "../../Container"
import { Link } from "../../Link"
import { Notice } from "../../Notice"
import { DetailsFilter } from "../EventState"
import { asPrice, formatDatesLong, formatDatesShort } from "../EventUtils"
import { EventDetailsLayoutProps } from "./EventDetailsState"
import { extractSingles, linkFor, locationString } from "./EventDetailsUtils"
import React = require("react")


const fallbackImage: Image = {
}

export const EventDetailsLayout = (props: EventDetailsLayoutProps) => {
  const { Jumbo, event, pageTitle, notice } = props
  const { Title, Description, BodyText, Kind, SiblingEvents, ChildEvents, Start, End, Locations } = event
  const { Header, Header2x, AltText, Original } = event.Image || fallbackImage

  const firstLocation = Locations[0]

  document.title = pageTitle

  return <div className="event-page" key={props.eventId}>
    <Jumbo key={slugify(Original)}
      title={Title}
      imageUrl={Header}
      imageUrlLarge={Header2x}
      imageAlt={AltText}
      size={props.eventsOverviewClassNames.jumboSize}
      contents={<>
        {Text(Description)}
        {Kind != 'repetition' &&
          <div className="header-item__meta">
            <span className="sr-text">Waar en wanneer: </span>
            <time dateTime={moment(Start).format('YYYY-MM-DD')}>
              {formatDatesShort(event)}
              <svg className="edition__icon" width="15" height="17" aria-hidden="true"><use xlinkHref="#icon--calendar" /></svg>
            </time>
            <address>
              {firstLocation.Name}
              <svg className="edition__icon" width="15" height="18" aria-hidden="true"><use xlinkHref="#icon--location" /></svg>
            </address>
          </div>}
        {Kind == 'single' && <ItemTickets {...event} />}
      </>}
    />

    <Container className="container--lg">
      {notice && <Container><Notice {...notice} /></Container>}

      {BodyText && <Container><Text {...BodyText} /></Container>}

      {SiblingEvents && seriesAlternatives(SiblingEvents)}
      {ChildEvents && listEvents(ChildEvents)}
    </Container>
  </div>
}

const seriesAlternatives = (events: EventDetails[]) => {
  if (events.length == 0) return <></>
  return <>
    <hr />
    <Container>
      <h3>Andere activiteiten in deze reeks:</h3>
      <div className="events__grid" data-columns="1">
        {events.map((e) =>
          <React.Fragment key={e.PrefixedId}>
            <div><Link {...linkFor(e)}>{e.Title} op {moment(e.Start).calendar()}{locationStringFromEvent(e, ', ')}</Link></div>
          </React.Fragment>
        )}
      </div>
    </Container>
  </>
}

const listEvents = (events: EventDetails[]) => {
  const config = features().events
  if (config == 'off' || events.length == 0) return <></>
  const singles = List(extractSingles(events))
    .sortBy(e => e.Start)
    .takeWhile(config.detailPage.filterChildren)
    .toList()
  const locationCount = singles.flatMap(e => e.Locations?.map(l => l.Name)).toSet().count()
  const regionCount = singles.flatMap(e => e.Locations?.map(l => l.Region)).toSet().count()

  // Calculate the distance between the preferred group size
  // and the average group size for each potential grouping
  const target = config.detailPage.eventGroupingTarget
  const ungrouped = Math.abs(target - singles.count())
  const perLocation = Math.abs(target - singles.count() / locationCount)
  const perRegion = Math.abs(target - singles.count() / regionCount)

  // Pick the grouping that is closest to the preferred group size
  const closest = Math.min(ungrouped, perLocation, perRegion)

  // Construct the appropriate event list based on the calculated "best" grouping
  const groupFilter = config.detailPage.filterGroupedChildren
  const list =
    closest == ungrouped ? simpleList(singles, groupFilter) :
    closest == perLocation ? perLocationList(singles, groupFilter) :
    perRegionList(singles, groupFilter)

  return <Container className="event" children={list} />
}

const simpleList = (events: List<EventDetails>, groupFilter: DetailsFilter) => {
  const locationPerEvent = events.flatMap(e => e.Locations?.map(l => l.Name)).toSet().count() > 1
  const regionPerEvent = events.flatMap(e => e.Locations?.map(l => l.Region)).toSet().count() > 1
  const loc: Location = events.first()?.Locations[0]
  const locString = locationString(loc)

  const header = locationPerEvent
    ? "Je kunt deze activiteit bezoeken op:"
    : locString.toLowerCase().trim() == 'online'
      ? 'Je kunt online deelnemen op:'
      : `Je kunt deze activiteit bezoeken in ${locString} op:`

  const hasAnyTickets = !events.flatMap(e => e.Tickets).isEmpty()

  return <Container className="events-list events-list--simple">
    <h3>{header}</h3>
    <div className="events__grid" data-columns={hasAnyTickets ? 2 : 1}>
      {events.takeWhile(groupFilter).map((e, i) => {
        const hasTickets = e.Tickets?.length > 0
        return <React.Fragment key={e.PrefixedId}>
          <div><ItemDate {...e} />{locationPerEvent && <ItemLocation {...e} includeRegion={regionPerEvent} />}.</div>
          {hasTickets && <div><ItemTickets {...e} /></div>}
        </React.Fragment>
      })}
    </div>
  </Container>
}

const perLocationList = (events: List<EventDetails>, groupFilter: DetailsFilter) => {
  const locations = events.flatMap<number, string>(e => e.Locations ? e.Locations.map(l => l.Name) : []).sort().toOrderedSet()
  const hasAnyTickets = !events.flatMap(e => e.Tickets).isEmpty()

  return <Container className="events-list events-list--per-location">
    {locations.map(location => <div className="event--location" key={location}>
      <h3>{location}</h3>
      <div className="events__grid" data-columns={hasAnyTickets ? 2 : 1}>{
        events
          .filter(e => !!List(e.Locations).find(l => l.Name == location))
          .takeWhile(groupFilter)
          .map((e, i) => {
            const hasTickets = e.Tickets?.length > 0
            return <React.Fragment key={`${location}--${e.PrefixedId}`}>
              <div><ItemDate {...e} />.</div>
              {hasTickets && <div><ItemTickets {...e} /></div>}
            </React.Fragment>
          })
          .toArray()
      }</div>
    </div>)}
  </Container>
}

const perRegionList = (events: List<EventDetails>, groupFilter: DetailsFilter) => {
  const regions = events.flatMap<number, string>(e => e.Locations ? e.Locations.map(l => l.Region) : []).sort().toOrderedSet()
  const hasAnyTickets = !events.flatMap(e => e.Tickets).isEmpty()

  return <Container className="events-list events-list--per-region">
    {regions.map(region => <div key={region}>
      <h3>{region}</h3>
      <div className="events__grid" data-columns={hasAnyTickets ? 2 : 1}>{
        events
          .filter(e => !!List(e.Locations).find(l => l.Region == region))
          .takeWhile(groupFilter)
          .map((e, i) => {
            const hasTickets = e.Tickets?.length > 0

            return <React.Fragment key={`${region}--${e.PrefixedId}`}>
              <div><ItemDate {...e} /><ItemLocation {...e} />.</div>
              {hasTickets && <div><ItemTickets {...e} /></div>}
            </React.Fragment>
          })
          .toArray()
      }</div>
    </div>)}
  </Container>
}

const ItemDate = ({ Start, End }: { Start?: Date, End?: Date }) => {
  if (!Start) return <></>
  const dateTimeString = capitalize(formatDatesLong({ Start, End }))
  return <span className="event__date">{dateTimeString}</span>
}

const ItemTickets = (p: { Id: string, Tickets?: Ticket[] }) => p.Tickets && p.Tickets.length > 0 && <div className="event__tickets">
  {p.Tickets.map((t, i) => <Link className="button button--primary button--event-ticket event__ticket" url={t.Uri} key={`ticket-${p.Id}-${i}`}>
    <span className="ticket__description">{t.Description}</span>
    {t.Cost ? <span className="ticket__cost">{asPrice(t.Cost)}</span> : <></>}
  </Link>)}
</div>

const ItemLocation = (p: { Locations?: Location[], includeRegion?: boolean }) =>
  p.Locations?.length == 1
    ? <span>{locationStringFrom(p.Locations[0], ' in ', p.includeRegion)}</span>
    : <></>

const locationStringFrom = (loc: Location, prefix = '', includeRegion = true) => {
  let location = ''
  if (loc?.Name) {
    location = `${prefix}${loc.Name}`
    if (includeRegion && loc.Region && !location.toLocaleLowerCase().includes(loc.Region.toLocaleLowerCase())) {
      location = `${location} (${loc.Region})`
    }
  }
  return location
}

const locationStringFromEvent = (event: EventDetailsFrontend, prefix = '') => locationStringFrom(event.Locations[0], prefix)
